import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  Box,
  styled,
  Typography,
  CircularProgress,
  IconButton,
  Popover,
  MenuItem,
  Modal,
} from "@mui/material";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthProvider";
import { CiEdit } from "react-icons/ci";
import { MdArchive, MdClose } from "react-icons/md";
import { RxCross1 } from "react-icons/rx";
import { TbAlertHexagonFilled } from "react-icons/tb";
import { BiImport } from "react-icons/bi";
import DryRoomImportModal from "./DryRoomImportModal";

const formatFloat = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return 0;
  return num.toFixed(2);
};

// ---------- INLINE EDIT COMPONENT ----------
const InlineEditInput = ({ value, onChange, type = "number" }) => {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        padding: "8px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        outline: "none",
      }}
    />
  );
};

const DryRoom = ({ roomId }) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedGrowthPhaseId, setSelectedGrowthPhaseId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const editValuesRef = useRef(editValues);
  const editingIdRef = useRef(editingId);

  useEffect(() => {
    editValuesRef.current = editValues;
  }, [editValues]);

  useEffect(() => {
    editingIdRef.current = editingId;
  }, [editingId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await httpService.get(`dry-room/${roomId}`);
      if (res?.data) {
        setData(res.data);
      }
    } catch (error) {
      console.error("Error fetching dry room data:", error);
      toast.error("Failed to fetch dry room data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [roomId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingIdRef.current;
      const currentValues = editValuesRef.current;

      if (
        !currentId ||
        !currentValues ||
        Object.keys(currentValues).length === 0
      )
        return;

      const isPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiPickersPopper-root, .MuiPickersLayout-root",
      );
      if (isPopover) return;

      if (!document.body.contains(event.target)) return;

      const isInsideEditing = event.target.closest(
        `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
      );

      if (!isInsideEditing) {
        handleSave(currentValues);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditValues({ ...row });
  };

  const handleFieldChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleImport = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const queryParams = {
        growth_phase_id: selectedGrowthPhaseId,
        roomId: roomId,
      };

      if (selectedId && selectedId > 0) {
        queryParams.id = selectedId;
      } else {
        queryParams.id = "";
      }

      const res = await httpService.post(
        "dry-room/import",
        formData,
        queryParams,
      );
      if (res?.success) {
        toast.success("File imported successfully");
        fetchData();
      } else {
        toast.error(res?.message || "Import failed");
      }
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (valuesToSave) => {
    setLoading(true);
    try {
      const currentValues = valuesToSave || editValues;
      const currentId = editingIdRef.current || editingId;

      const payload = {
        temp_target: parseFloat(currentValues.temp_target) || 0,
        high_temp: parseFloat(currentValues.high_temp) || 0,
        low_temp: parseFloat(currentValues.low_temp) || 0,
        avg_temp: parseFloat(currentValues.avg_temp) || 0,
        humidity_target: parseFloat(currentValues.humidity_target) || 0,
        high_humidity: parseFloat(currentValues.high_humidity) || 0,
        low_humidity: parseFloat(currentValues.low_humidity) || 0,
        avg_humidity: parseFloat(currentValues.avg_humidity) || 0,
        growth_phase_id: currentValues.growth_phase_id,
      };

      if (currentId < 0) {
        await httpService.post(`dry-room/init/${roomId}`, payload);
        toast.success("Created successfully");
      } else {
        await httpService.put(`dry-room/${currentId}`, payload);
        toast.success("Updated successfully");
      }
      setEditingId(null);
      setEditValues({});
      fetchData();
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveAll = async () => {
    setLoading(true);
    try {
      await httpService.put(`dry-room/archive/${roomId}`);
      toast.success("All logs archived successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive logs");
    } finally {
      setLoading(false);
    }
  };

  const StyledGridOverlay = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }));

  const getColorStyle = (color) => {
    const colors = {
      RED: "#ffebee",
      YELLOW: "#fff9c4",
      GREEN: "#e8f5e9",
    };
    return colors[color] || "transparent";
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {editingId === params.row.id ? (
            <IconButton
              onClick={() => {
                setEditingId(null);
                setEditValues({});
              }}
              color="error"
            >
              <MdClose />
            </IconButton>
          ) : (
            <>
              <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
                <GridMoreVertIcon />
              </IconButton>
              {anchorEl && menuRowId === params.row.id && (
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleCloseMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiPaper-root": {
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                      borderRadius: "8px",
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleEdit(params.row);
                      handleCloseMenu();
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 1,
                      px: 2,
                    }}
                  >
                    <Typography variant="body2">Edit</Typography>
                    <CiEdit size={18} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedGrowthPhaseId(params.row.growth_phase_id);
                      setSelectedId(params.row.id);
                      setImportModalOpen(true);
                      handleCloseMenu();
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 1,
                      px: 2,
                    }}
                  >
                    <Typography variant="body2">Import</Typography>
                    <BiImport size={18} />
                  </MenuItem>
                </Popover>
              )}
            </>
          )}
        </>
      ),
    },
    {
      field: "phase_name",
      headerName: "Growth Phase",
      flex: 1,
      minWidth: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "temp_target",
      headerName: "Temp Target",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.temp_target) ?? ""}
            onChange={(e) => handleFieldChange("temp_target", e.target.value)}
          />
        ) : (
          <div style={{ width: "100%" }}>{parseFloat(params.value) || ""}</div>
        ),
    },
    {
      field: "high_temp",
      headerName: "High Temp",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.high_temp) ?? ""}
            onChange={(e) => handleFieldChange("high_temp", e.target.value)}
          />
        ) : (
          <div
            style={{
              backgroundColor: params.value
                ? getColorStyle(params.row.high_temp_color)
                : "unset",
              padding: "8px",
              width: "100%",
            }}
          >
            {parseFloat(params.value) || ""}
          </div>
        ),
    },
    {
      field: "low_temp",
      headerName: "Low Temp",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.low_temp) ?? ""}
            onChange={(e) => handleFieldChange("low_temp", e.target.value)}
          />
        ) : (
          <div
            style={{
              backgroundColor:
                params.value > 0
                  ? getColorStyle(params.row.low_temp_color)
                  : "unset",
              padding: "8px",
              width: "100%",
            }}
          >
            {parseFloat(params.value) || ""}
          </div>
        ),
    },
    {
      field: "avg_temp",
      headerName: "Average Temp",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.avg_temp) ?? ""}
            onChange={(e) => handleFieldChange("avg_temp", e.target.value)}
          />
        ) : (
          <div
            style={{
              backgroundColor:
                params.value > 0
                  ? getColorStyle(params.row.avg_temp_color)
                  : "unset",
              padding: "8px",
              width: "100%",
            }}
          >
            {parseFloat(params.value) || ""}
          </div>
        ),
    },
    {
      field: "humidity_target",
      headerName: "Humidity Target",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.humidity_target) ?? ""}
            onChange={(e) =>
              handleFieldChange("humidity_target", e.target.value)
            }
          />
        ) : (
          <div style={{ width: "100%" }}>{parseFloat(params.value) || ""}</div>
        ),
    },
    {
      field: "high_humidity",
      headerName: "High Humidity",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.high_humidity) ?? ""}
            onChange={(e) => handleFieldChange("high_humidity", e.target.value)}
          />
        ) : (
          <div
            style={{
              backgroundColor:
                params.value > 0
                  ? getColorStyle(params.row.high_humidity_color)
                  : "unset",
              padding: "8px",
              width: "100%",
            }}
          >
            {parseFloat(params.value) || ""}
          </div>
        ),
    },
    {
      field: "low_humidity",
      headerName: "Low Humidity",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.low_humidity) ?? ""}
            onChange={(e) => handleFieldChange("low_humidity", e.target.value)}
          />
        ) : (
          <div
            style={{
              backgroundColor:
                params.value > 0
                  ? getColorStyle(params.row.low_humidity_color)
                  : "unset",
              padding: "8px",
              width: "100%",
            }}
          >
            {parseFloat(params.value) || ""}
          </div>
        ),
    },
    {
      field: "avg_humidity",
      headerName: "Average Humidity",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingId === params.row.id ? (
          <InlineEditInput
            value={parseFloat(editValues.avg_humidity) ?? ""}
            onChange={(e) => handleFieldChange("avg_humidity", e.target.value)}
          />
        ) : (
          <div
            style={{
              backgroundColor:
                params.value > 0
                  ? getColorStyle(params.row.avg_humidity_color)
                  : "unset",
              padding: "8px",
              width: "100%",
            }}
          >
            {parseFloat(params.value) || ""}
          </div>
        ),
    },
  ];

  return (
    <div className="company">
      <div className="page-title pb-0 pt-4">
        <div className="title-wrap">{/* <h1>Room Log History</h1> */}</div>
        <div className="button-wrap mb-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setArchiveModalOpen(true)}
          >
            <MdArchive /> Archive
          </button>
        </div>
      </div>

      <div
        className="common_content"
        style={{
          height: `${data?.length > 0 ? "" : "200px"}`,
          width: "100%",
          marginTop: 12,
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            border: "1px solid #E0E0E0",
            [`& .${gridClasses.cell}`]: { py: 1 },
            // height: "calc(100vh - 350px)",
            // minHeight: 576,
          }}
          hideFooter
          showCellVerticalBorder
          showColumnVerticalBorder
          autoHeight
          slots={{
            noRowsOverlay: () => (
              <StyledGridOverlay>
                <Typography variant="body2" color="textSecondary">
                  No Data Found
                </Typography>
              </StyledGridOverlay>
            ),
          }}
        />
      </div>

      <Modal
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        aria-labelledby="archive-modal-title"
        aria-describedby="archive-modal-description"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Are you Sure?</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={() => setArchiveModalOpen(false)} />
            </div>
          </div>
          <div className="modal_content">
            <div className="modal_content_inner">
              <div>
                <TbAlertHexagonFilled />
              </div>
              <div>
                <p>Do you want to archive?</p>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => setArchiveModalOpen(false)}
              className="btn btn-border"
            >
              No
            </button>
            <button
              onClick={() => {
                handleArchiveAll();
                setArchiveModalOpen(false);
              }}
              className="btn btn-primary"
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <DryRoomImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImport}
      />
    </div>
  );
};

export default DryRoom;
